import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { EmptyState, Spinner, cn, useI18n } from '@xdocs/ui';
import type { Translator } from '@xdocs/i18n';
import { useAuth } from '../auth/AuthProvider.js';
import { AuthTransportError } from '../auth/authTransport.js';
import { safeT } from '../i18n/safeT.js';
import styles from './AuthCallbackPage.module.css';

type CallbackErrorKind = 'oauthDenied' | 'oauthFailed' | 'invalidState' | 'generic';

/** Maps `GET /auth/google/callback`'s `#error=<code>` (`apps/server/src/
 * routes/oauth.ts`) to one of our four catalog messages — everything Google/
 * our own server can hand back that isn't one of the two named failure modes
 * falls back to the generic message rather than leaking a raw error code. */
function parseHashErrorKind(hash: string): CallbackErrorKind | null {
  const code = new URLSearchParams(hash.replace(/^#/, '')).get('error');
  if (!code) return null;
  if (code === 'access_denied') return 'oauthDenied';
  if (code === 'invalid_oauth_state') return 'invalidState';
  if (code === 'oauth_exchange_failed') return 'oauthFailed';
  return 'generic';
}

function translateErrorKind(t: Translator['t'], kind: CallbackErrorKind): string {
  switch (kind) {
    case 'oauthDenied':
      return t('auth:callback.error.oauthDenied');
    case 'invalidState':
      return t('auth:callback.error.invalidState');
    case 'oauthFailed':
      return t('auth:callback.error.oauthFailed');
    case 'generic':
      return t('auth:callback.error.generic');
  }
}

/**
 * `/auth/callback` (F12/F43, design/app mockup 16 pixel parity for the
 * loading state — the mockup only demos the spinner, not an error state, so
 * the error UI below is designed to match the sibling auth screens instead).
 * Two producers land here:
 *
 * 1. **Google OAuth** (`GET /auth/google/callback` 302s here with
 *    `#accessToken=…`/`#error=…`) — the refresh cookie is ALREADY set by
 *    that redirect response, so this page does nothing but watch
 *    `AuthProvider`'s own boot-time silent-refresh settle to `authenticated`
 *    (it reads that same now-valid cookie independently); the hash's
 *    `accessToken` itself is never read here — see `AuthProvider.tsx`'s
 *    `bootRefresh`. An explicit `#error=…` short-circuits straight to the
 *    error state without waiting on that refresh.
 * 2. **Magic link** (email button → `/auth/callback?token=…`, F12 path 2) —
 *    this page drives `auth.magicLink.consume` itself via
 *    `useAuth().consumeMagicLink`, the one WS round-trip that establishes a
 *    session the same way `login`/`signup` do.
 */
export function AuthCallbackPage(): ReactElement {
  const { t, locale } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const { status, consumeMagicLink } = useAuth();
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);
  const attempted = useRef(false);

  const magicToken = new URLSearchParams(location.search).get('token');
  const hashErrorKind = parseHashErrorKind(location.hash);
  const isMagicLinkFlow = Boolean(magicToken);

  useEffect(() => {
    if (!magicToken || attempted.current) return;
    attempted.current = true;
    consumeMagicLink({ token: magicToken }).catch((err: unknown) => {
      const message =
        err instanceof AuthTransportError
          ? safeT(locale, err.alert.message)
          : safeT(locale, 'auth:callback.error.generic');
      setMagicLinkError(message);
    });
  }, [magicToken, consumeMagicLink, locale]);

  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  let errorMessage: string | null = null;
  if (magicLinkError) {
    errorMessage = magicLinkError;
  } else if (hashErrorKind) {
    errorMessage = translateErrorKind(t, hashErrorKind);
  } else if (!isMagicLinkFlow && status === 'unauthenticated') {
    // Landed here with no token, no hash error, and the boot-time silent
    // refresh still came back unauthenticated — nothing this page can do.
    errorMessage = t('auth:callback.error.generic');
  }

  if (errorMessage) {
    return (
      <div className={cn(styles['page'])}>
        <EmptyState
          kind="error"
          title={t('auth:callback.error.title')}
          description={errorMessage}
          action={{ label: t('auth:callback.backToLogin'), onClick: () => void navigate('/login') }}
          mascotAlt=""
        />
      </div>
    );
  }

  return (
    <div className={cn(styles['page'])}>
      <Spinner size={32} className={styles['spinner']} />
      <p className={cn(styles['label'])}>{t('auth:callback.title')}</p>
    </div>
  );
}
